import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, CheckCheck, User, ChevronDown, LogOut, Menu, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import AgbazaButton from "../../../components/atoms/button/Button";
import { cn } from "../../../lib/utils";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import {
  getNotificationsForProfessionnel,
  markAllNotificationsRead,
  markNotificationRead,
  type ProNotification,
} from "../../../api/professional";

interface Notification {
  id: string;
  title: string;
  by: string;
  time: string;
  read: boolean;
  type?: string;
}

interface ProDashboardLayoutProps {
  children: ReactNode;
}

const sidebarCancelClass = (active: boolean) =>
  cn(
    "!mt-0 w-full justify-start px-3 py-2 text-sm font-medium laptop:rounded-[8px]",
    active ? "border-l-4 border-[#00553E] bg-[#DCEFE9]/30" : ""
  );

export default function ProDashboardLayout({ children }: ProDashboardLayoutProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { professional, signOut } = useProfessionalAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");
  const [notifs, setNotifs] = useState<Notification[]>([]);
  const [notifsLoading, setNotifsLoading] = useState(false);

  const mapProNotification = useCallback((n: ProNotification, index: number): Notification => {
    const id = String(n.id ?? `n-${index}`);
    const title = String(n.titre ?? n.title ?? n.message ?? n.contenu ?? "Notification");
    const rawDate = n.dateCreation ?? n.date;
    const time =
      typeof rawDate === "string" && rawDate
        ? new Date(rawDate).toLocaleString("fr-FR")
        : "";
    const read = Boolean(n.read ?? n.statut === "lu");
    return { id, title, by: "", time, read, type: n.type };
  }, []);

  const loadNotifications = useCallback(async () => {
    const nip = professional?.nip?.trim();
    if (!nip) {
      setNotifs([]);
      return;
    }
    setNotifsLoading(true);
    const res = await getNotificationsForProfessionnel(nip, { limit: 40, offset: 0 });
    setNotifsLoading(false);
    if (!res.ok) {
      setNotifs([]);
      return;
    }
    setNotifs(res.data.map(mapProNotification));
  }, [professional?.nip, mapProNotification]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const displayName = useMemo(() => {
    if (professional?.nom || professional?.prenom) {
      return `${professional?.nom ?? ""} ${professional?.prenom ?? ""}`.trim();
    }
    return "Professionnel";
  }, [professional]);

  const navActivite = pathname.startsWith("/pro/dashboard/mon-activite");
  const navPatients = pathname.startsWith("/pro/dashboard/patients");
  const navDiag = pathname === "/pro/dashboard/diagnostic-rapide";
  const navConsultationsAssignees = pathname.startsWith("/pro/dashboard/consultations-assignees");
  const navVerif = pathname.startsWith("/pro/dashboard/verification-identite");

  const unreadCount = notifs.filter((notif) => !notif.read).length;
  const listForTab = notifs.filter((notif) => (notifTab === "all" ? true : !notif.read));
  const markAllRead = async () => {
    const nip = professional?.nip?.trim();
    if (!nip || !unreadCount) return;
    const res = await markAllNotificationsRead(nip);
    if (res.ok) setNotifs((prev) => prev.map((notif) => ({ ...notif, read: true })));
  };

  const markOneRead = async (notificationId: string) => {
    const res = await markNotificationRead(notificationId);
    if (res.ok) {
      setNotifs((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/professionnel/connexion");
  };

  return (
    <div className="grid h-screen grid-cols-1 grid-rows-1 overflow-hidden md:grid-cols-12">
      <aside className="hidden h-full min-h-0 overflow-hidden border-r bg-white p-4 md:flex md:col-span-3 md:p-6 lg:col-span-2 flex-col" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center justify-between">
          <div className="relative">
            <Button variant="ghost" onClick={() => setMenuOpen((open) => !open)} className="flex items-center gap-2 text-[#0B1F16] hover:bg-[#F3F4F4]">
              <div className="font-semibold">{displayName}</div>
              <ChevronDown size={16} className="text-gray-600" />
            </Button>
            {menuOpen && (
              <div
                className="absolute left-0 z-30 mt-2 w-56 min-w-[14rem] rounded-[10px] border bg-white p-3 shadow-md"
                style={{ borderColor: "#E5E7EB" }}
              >
                <div className="flex flex-col gap-6">
                  <AgbazaButton
                    variant="cancel"
                    expandWidth
                    className={sidebarCancelClass(false)}
                    style={{ height: "auto", minHeight: 44 }}
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/pro/dashboard/profil");
                    }}
                  >
                    Profil
                  </AgbazaButton>
                  <AgbazaButton
                    variant="cancel"
                    cancelCustomColor
                    expandWidth
                    className="!mt-0 w-full justify-start px-3 py-2 text-sm font-medium laptop:rounded-[8px]"
                    style={{ height: "auto", minHeight: 44 }}
                    onClick={() => {
                      void handleSignOut();
                      setMenuOpen(false);
                    }}
                  >
                    Déconnexion
                  </AgbazaButton>
                </div>
              </div>
            )}
          </div>
          <div className="hidden md:block" />
          <Button variant="ghost" size="icon" aria-label="Notifications" className="text-[#0B1F16] relative md:hidden" onClick={() => setNotifOpen((open) => !open)}>
            <Bell size={18} />
            {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
          </Button>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <nav className="flex flex-col gap-6">
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navActivite)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/pro/dashboard/mon-activite")}
            >
              Mon activité
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navPatients)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/pro/dashboard/patients")}
            >
              Gestion des patients
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navDiag)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/pro/dashboard/diagnostic-rapide")}
            >
              Consultation Rapide
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navConsultationsAssignees)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/pro/dashboard/consultations-assignees")}
            >
              Consultations assignées
            </AgbazaButton>
            <AgbazaButton
              variant="cancel"
              expandWidth
              className={sidebarCancelClass(navVerif)}
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => navigate("/pro/dashboard/verification-identite")}
            >
              Vérification d&apos;identité
            </AgbazaButton>
          </nav>
        </div>
        <AgbazaButton
          variant="cancel"
          cancelCustomColor
          expandWidth
          className="!mt-auto flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
          style={{ height: "auto", minHeight: 44 }}
          onClick={() => void handleSignOut()}
        >
          <LogOut className="size-4 shrink-0" aria-hidden />
          <span>Déconnexion</span>
        </AgbazaButton>
      </aside>

      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white border-r p-4 flex flex-col" style={{ borderColor: "#E5E7EB" }}>
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[#0B1F16]">{displayName}</div>
              <Button variant="ghost" size="icon" aria-label="Fermer" className="text-[#0B1F16]" onClick={() => setMobileNavOpen(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="mt-4">
              <nav className="flex flex-col gap-6">
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navActivite)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/pro/dashboard/mon-activite");
                  }}
                >
                  Mon activité
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navPatients)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/pro/dashboard/patients");
                  }}
                >
                  Gestion des patients
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navDiag)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/pro/dashboard/diagnostic-rapide");
                  }}
                >
                  Consultation Rapide
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navConsultationsAssignees)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/pro/dashboard/consultations-assignees");
                  }}
                >
                  Consultations assignées
                </AgbazaButton>
                <AgbazaButton
                  variant="cancel"
                  expandWidth
                  className={sidebarCancelClass(navVerif)}
                  style={{ height: "auto", minHeight: 44 }}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/pro/dashboard/verification-identite");
                  }}
                >
                  Vérification d&apos;identité
                </AgbazaButton>
              </nav>
            </div>
            <AgbazaButton
              variant="cancel"
              cancelCustomColor
              expandWidth
              className="!mt-auto flex w-full items-center justify-start gap-2 px-3 py-2 text-sm"
              style={{ height: "auto", minHeight: 44 }}
              onClick={() => void handleSignOut()}
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              <span>Déconnexion</span>
            </AgbazaButton>
          </div>
        </div>
      )}

      <main className="relative h-full min-h-0 w-full overflow-y-auto md:col-span-9 lg:col-span-10">
        <div className="relative flex min-h-full w-full flex-col">
          <img src="/steto.jpg" alt="" aria-hidden className="pointer-events-none absolute inset-0 z-0 h-full w-full min-h-full object-cover" />
          <div className="pointer-events-none absolute inset-0 z-0 bg-black/20" />

          <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col px-4 py-4 md:px-6 md:py-5">
            {/* Top translucent header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/agb.png" alt="agabaza" className="h-9 w-auto" />
              </div>
              <div className="flex items-center gap-6 text-white">
                <button aria-label="Notifications" className="relative" onClick={() => setNotifOpen((open) => !open)}>
                  <Bell size={20} />
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
                </button>
                <div className="flex items-center gap-2">
                  <User size={20} />
                  <span className="font-semibold">{displayName}</span>
                  <ChevronDown size={16} />
                </div>
                <button aria-label="Menu" className="md:hidden" onClick={() => setMobileNavOpen(true)}>
                  <Menu size={20} />
                </button>
              </div>
            </div>

            {/* Notifications dropdown */}
            {notifOpen && (
              <div className="absolute right-6 top-16 z-50 w-80 sm:w-96 rounded-[10px] border bg-white shadow" style={{ borderColor: "#E5E7EB" }}>
                <div className="px-5 pb-3 pt-4">
                  <div className="text-center font-semibold">Centre de notifications</div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:bg-[#F3FAF7]"
                        style={
                          notifTab === "all"
                            ? { background: "#DCEFE9", color: "#00553E", fontWeight: 600 }
                            : { color: "#6B7280" }
                        }
                        onClick={() => setNotifTab("all")}
                      >
                        Toutes
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="hover:bg-[#F3FAF7]"
                        style={
                          notifTab === "unread"
                            ? { background: "#DCEFE9", color: "#00553E", fontWeight: 600 }
                            : { color: "#6B7280" }
                        }
                        onClick={() => setNotifTab("unread")}
                      >
                        Non lues
                        {unreadCount > 0 && (
                          <Badge variant="soft" className="ml-1.5">
                            {unreadCount}
                          </Badge>
                        )}
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void markAllRead()}
                      className="hover:bg-[#F3FAF7]"
                      style={{ color: unreadCount ? "#0B9D78" : "#D1D5DB" }}
                      disabled={!unreadCount}
                    >
                      <CheckCheck size={14} />
                      Tout marquer comme lues
                    </Button>
                  </div>
                </div>
                <div className="border-t" style={{ borderColor: "#E5E7EB" }} />
                <div className="max-h-[360px] overflow-auto">
                  {notifsLoading ? (
                    <div className="px-5 py-10 text-center text-gray-600">Chargement…</div>
                  ) : listForTab.length === 0 ? (
                    <div className="px-5 py-10 text-center text-gray-600">Vous n’avez aucune notification.</div>
                  ) : (
                    listForTab.map((notif) => {
                      const isRemoval = notif.type === "retrait_etablissement";
                      return (
                        <button
                          key={notif.id}
                          type="button"
                          className={`w-full px-5 py-3 text-left text-sm ${
                            isRemoval ? "bg-red-50" : !notif.read ? "bg-[#DCEFE9]" : ""
                          }`}
                          style={isRemoval ? { borderLeft: "3px solid #B91C1C" } : undefined}
                          onClick={() => {
                            if (!notif.read) void markOneRead(notif.id);
                          }}
                        >
                          <div className={`flex items-start gap-2 ${isRemoval ? "text-red-800" : "text-gray-800"}`}>
                            {isRemoval && <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                            <span>{notif.title}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">{notif.time}</div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Page content */}
            <div className="mt-4 md:mt-6">
              {children}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

